import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquareText, UserPlus, Users } from "lucide-react";
import apiClient from "@/api/client";
import { formatDateTime } from "@/utils/formatDate";
import { useCreateRoom, useGlobalMessageSearch } from "../api/chatApi";
import type { ChatMessage, ChatRoomBrief, ChatUserBrief } from "../types";
import { Avatar } from "./Avatar";

interface InternalUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  branch: { id: string; name: string } | null;
  profile_picture_url: string | null;
}

interface GlobalSearchPanelProps {
  query: string;
  rooms: ChatRoomBrief[];
  currentUserId: string | undefined;
  onSelectRoom: (roomId: string) => void;
  onOpenMessage: (roomId: string, messageId: string) => void;
}

function otherMember(
  members: ChatUserBrief[],
  currentUserId: string | undefined,
): ChatUserBrief | null {
  return members.find((m) => m.id !== currentUserId) ?? members[0] ?? null;
}

function roomTitle(
  room: ChatRoomBrief,
  currentUserId: string | undefined,
): string {
  if (room.kind === "direct") {
    const other = otherMember(room.members ?? [], currentUserId);
    return other?.full_name || other?.username || "Rozmowa";
  }
  return room.name || "Pokój grupowy";
}

function snippet(body: string, term: string): string {
  if (!body) return "";
  const idx = body.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return body.slice(0, 120);
  const start = Math.max(0, idx - 40);
  const end = Math.min(body.length, idx + term.length + 80);
  return (
    (start > 0 ? "…" : "") + body.slice(start, end) + (end < body.length ? "…" : "")
  );
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * Replaces the room list while the user is typing in the search box.
 * People (existing conversations + directory users without one) come first,
 * then message-content hits across all conversations.
 */
export function GlobalSearchPanel({
  query,
  rooms,
  currentUserId,
  onSelectRoom,
  onOpenMessage,
}: GlobalSearchPanelProps) {
  const term = query.trim().toLowerCase();
  const debouncedQuery = useDebounced(query, 250);
  const contentSearch = useGlobalMessageSearch(debouncedQuery);
  const create = useCreateRoom();

  // Same key + endpoint as NewRoomDialog, so the cache is shared.
  const users = useQuery({
    queryKey: ["chat", "internal-users"],
    queryFn: async () => {
      const r = await apiClient.get<{ data: InternalUser[] }>(
        "/auth/users/?selectable=true",
      );
      return r.data.data ?? [];
    },
  });

  const matchedRooms = useMemo(() => {
    if (!term) return [];
    return rooms
      .filter((r) =>
        roomTitle(r, currentUserId).toLowerCase().includes(term) ||
        (r.kind === "direct" &&
          (otherMember(r.members ?? [], currentUserId)?.username ?? "")
            .toLowerCase()
            .includes(term)),
      )
      .sort((a, b) => {
        // People (direct conversations) before group rooms.
        if (a.kind !== b.kind) return a.kind === "direct" ? -1 : 1;
        return roomTitle(a, currentUserId).localeCompare(
          roomTitle(b, currentUserId),
          "pl",
        );
      });
  }, [rooms, term, currentUserId]);

  const directPartnerIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rooms) {
      if (r.kind !== "direct") continue;
      for (const m of r.members ?? []) {
        if (m.id !== currentUserId) set.add(m.id);
      }
    }
    return set;
  }, [rooms, currentUserId]);

  const matchedNewUsers = useMemo(() => {
    if (!term) return [];
    return (users.data ?? [])
      .filter(
        (u) =>
          u.id !== currentUserId &&
          u.role !== "contractor" &&
          !directPartnerIds.has(u.id),
      )
      .filter(
        (u) =>
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(term) ||
          u.username.toLowerCase().includes(term),
      )
      .sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`,
          "pl",
        ),
      );
  }, [users.data, term, currentUserId, directPartnerIds]);

  const hits: ChatMessage[] = contentSearch.data ?? [];
  const roomById = useMemo(() => {
    const map = new Map<string, ChatRoomBrief>();
    for (const r of rooms) map.set(r.id, r);
    return map;
  }, [rooms]);

  async function startDirect(userId: string) {
    // Backend create is idempotent for direct pairs — returns the existing
    // room if one already exists.
    const room = await create.mutateAsync({
      kind: "direct",
      name: "",
      member_ids: [userId],
    });
    onSelectRoom(room.id);
  }

  const noPeople = matchedRooms.length === 0 && matchedNewUsers.length === 0;
  const contentReady = debouncedQuery.trim().length >= 2;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* ── People / conversations ── */}
      <div className="px-3 pt-2 pb-1 text-[10px] font-mono font-semibold uppercase tracking-wider text-gray-400">
        Osoby i rozmowy
      </div>
      {noPeople && (
        <div className="px-3 pb-2 text-xs text-gray-500">
          {users.isLoading ? "Szukam…" : "Brak pasujących osób."}
        </div>
      )}
      {matchedRooms.map((room) => {
        const other = otherMember(room.members ?? [], currentUserId);
        const title = roomTitle(room, currentUserId);
        const groupPic = room.picture?.url ?? null;
        return (
          <button
            key={room.id}
            type="button"
            onClick={() => onSelectRoom(room.id)}
            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
          >
            {room.kind === "direct" ? (
              <Avatar user={other} size={32} />
            ) : groupPic ? (
              <img
                src={groupPic}
                alt={title}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                <Users size={14} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {title}
              </div>
              <div className="text-[11px] text-gray-500 truncate">
                {room.kind === "direct" ? "Rozmowa 1-1" : "Pokój grupowy"}
              </div>
            </div>
            {room.unread_count > 0 && (
              <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center flex-shrink-0">
                {room.unread_count}
              </span>
            )}
          </button>
        );
      })}
      {matchedNewUsers.map((u) => {
        const full = `${u.first_name} ${u.last_name}`.trim() || u.username;
        return (
          <button
            key={u.id}
            type="button"
            disabled={create.isPending}
            onClick={() => startDirect(u.id)}
            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Avatar
              user={{
                full_name: full,
                username: u.username,
                profile_picture_url: u.profile_picture_url,
              }}
              size={32}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {full}
              </div>
              <div className="text-[11px] text-gray-500 truncate">
                {u.branch?.name || "—"}
              </div>
            </div>
            <span
              className="text-gray-400 flex-shrink-0"
              title="Rozpocznij rozmowę"
            >
              <UserPlus size={14} />
            </span>
          </button>
        );
      })}

      {/* ── Message content across all conversations ── */}
      <div className="px-3 pt-3 pb-1 text-[10px] font-mono font-semibold uppercase tracking-wider text-gray-400 border-t border-gray-100">
        Wiadomości
      </div>
      {!contentReady && (
        <div className="px-3 pb-2 text-xs text-gray-500">
          Wpisz min. 2 znaki, aby szukać w treści rozmów.
        </div>
      )}
      {contentReady && contentSearch.isFetching && (
        <div className="px-3 pb-2 text-xs text-gray-500 italic">Szukam…</div>
      )}
      {contentReady && !contentSearch.isFetching && hits.length === 0 && (
        <div className="px-3 pb-2 text-xs text-gray-500">
          Brak wiadomości z tą frazą.
        </div>
      )}
      {hits.map((m) => {
        const room = roomById.get(m.room);
        const senderName = m.sender?.full_name || m.sender?.username || "—";
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onOpenMessage(m.room, m.id)}
            className="w-full text-left px-3 py-1.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          >
            <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
              <span className="font-semibold text-gray-700 truncate flex items-center gap-1">
                <MessageSquareText size={11} className="flex-shrink-0" />
                {room ? roomTitle(room, currentUserId) : "Rozmowa"}
              </span>
              <span className="font-mono flex-shrink-0">
                {formatDateTime(m.created_at, "")}
              </span>
            </div>
            <div className="text-[12px] text-gray-800 truncate">
              <span className="text-gray-500">{senderName}: </span>
              {snippet(m.body, debouncedQuery.trim())}
            </div>
          </button>
        );
      })}
    </div>
  );
}
